import { useState, useRef, useEffect, useMemo } from "react";
import { ChevronDown, Check, Search } from "lucide-react";
import { useGeo } from "@/context/GeoContext";
import { CURRENCY_OPTIONS } from "@/lib/currency";

export default function CurrencySwitcher() {
    const { currencyPref, setCurrencyPref } = useGeo();
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState("");
    const ref = useRef(null);
    const inputRef = useRef(null);

    useEffect(() => {
        function onDocClick(e) {
            if (!ref.current || !ref.current.contains(e.target)) setOpen(false);
        }
        if (open) document.addEventListener("mousedown", onDocClick);
        return () => document.removeEventListener("mousedown", onDocClick);
    }, [open]);

    useEffect(() => {
        if (!open) setQuery("");
        else setTimeout(() => inputRef.current?.focus(), 0);
    }, [open]);

    const current = currencyPref || "USD";
    const showFilter = CURRENCY_OPTIONS.length > 8;

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return CURRENCY_OPTIONS;
        return CURRENCY_OPTIONS.filter(
            (c) =>
                c.code.toLowerCase().includes(q) ||
                c.name.toLowerCase().includes(q) ||
                c.country.toLowerCase().includes(q),
        );
    }, [query]);

    return (
        <div className="relative" ref={ref} data-testid="currency-switcher">
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                aria-label="Change currency"
                title="Switching currency also updates region & deals"
                data-testid="currency-switcher-button"
                className="inline-flex items-center gap-1 rounded-full border border-[#E5E5E5] bg-white px-2.5 py-1 text-[12px] font-medium tracking-tight text-[#0A0A0A] transition hover:border-[#0A0A0A]"
            >
                <span data-testid="currency-current">{current}</span>
                <ChevronDown size={12} strokeWidth={2.25} className="text-[#737373]" />
            </button>
            {open && (
                <div
                    role="menu"
                    data-testid="currency-menu"
                    className="absolute right-0 mt-2 w-64 overflow-hidden rounded-xl border border-[#E5E5E5] bg-white shadow-[0_8px_24px_rgba(0,0,0,0.06)]"
                >
                    <p className="border-b border-[#F5F5F5] px-3.5 py-2 text-[10px] uppercase tracking-wider text-[#9CA3AF]">
                        Switching also updates region & deals
                    </p>
                    {showFilter && (
                        <div className="flex items-center gap-2 border-b border-[#F5F5F5] px-3 py-2">
                            <Search size={12} strokeWidth={2.25} className="text-[#A3A3A3]" />
                            <input
                                ref={inputRef}
                                value={query}
                                onChange={(e) => setQuery(e.target.value)}
                                placeholder="Search currency, country…"
                                data-testid="currency-search-input"
                                className="w-full bg-transparent text-[12.5px] text-[#0A0A0A] outline-none placeholder:text-[#A3A3A3]"
                            />
                        </div>
                    )}
                    <div className="max-h-72 overflow-y-auto" data-testid="currency-options-scroll">
                        {filtered.length === 0 ? (
                            <p className="px-3.5 py-3 text-[12px] text-[#A3A3A3]">
                                No matches
                            </p>
                        ) : (
                            filtered.map((c) => {
                                const active = c.code === current;
                                return (
                                    <button
                                        key={c.code}
                                        type="button"
                                        onClick={() => {
                                            setCurrencyPref(c.code, { fromUser: true });
                                            setOpen(false);
                                        }}
                                        data-testid={`currency-option-${c.code}`}
                                        className={`flex w-full items-center justify-between gap-2 px-3.5 py-2 text-left text-[13px] transition hover:bg-[#F5F5F5] ${
                                            active ? "text-[#0A0A0A]" : "text-[#525252]"
                                        }`}
                                    >
                                        <span className="flex flex-col">
                                            <span className="font-medium">{c.label}</span>
                                            <span className="text-[10.5px] text-[#9CA3AF]">
                                                {c.name} · {c.country}
                                            </span>
                                        </span>
                                        {active && (
                                            <Check
                                                size={13}
                                                strokeWidth={2.5}
                                                className="text-[#0A0A0A]"
                                                data-testid={`currency-check-${c.code}`}
                                            />
                                        )}
                                    </button>
                                );
                            })
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
